import { useState, useEffect } from 'react'
import { evaluationAPI } from '../../services/api'

const SPEAKER_COLORS = {
  patient: 'border-l-orange-400',
  physician: 'border-l-blue-400',
  nurse: 'border-l-teal-400',
}

const SPEAKER_LABELS = {
  patient: 'Patient Agent',
  physician: 'Physician Agent',
  nurse: 'Nurse Agent',
}

const DIMENSION_LABELS = {
  clinical_domain_coverage: 'Clinical Domain Coverage',
  guideline_adherence: 'Guideline Adherence',
  red_flag_escalation: 'Red Flag Escalation',
  documentation_quality: 'Documentation Quality',
  shared_decision_making: 'Shared Decision Making',
  clinical_questioning: 'Clinical Questioning',
  follow_up_planning: 'Follow-up Planning',
  persona_fidelity: 'Persona Fidelity',
  coherence_and_context: 'Coherence & Context',
  naturalness: 'Naturalness',
}

const TIER_ORDER = [
  { tier: 'Clinical Validity', dims: ['clinical_domain_coverage', 'guideline_adherence', 'red_flag_escalation'] },
  { tier: 'Encounter Quality', dims: ['documentation_quality', 'shared_decision_making', 'clinical_questioning', 'follow_up_planning'] },
  { tier: 'Conversation Realism', dims: ['persona_fidelity', 'coherence_and_context', 'naturalness'] },
]

const PROVIDER_LABELS = { openai: 'OpenAI', anthropic: 'Anthropic', gemini: 'Gemini' }
const PROVIDER_BORDER_COLORS = { openai: 'border-l-emerald-500', anthropic: 'border-l-violet-500', gemini: 'border-l-amber-500' }
const PROVIDER_TEXT_COLORS = { openai: 'text-emerald-700', anthropic: 'text-violet-700', gemini: 'text-amber-700' }

function transformJudgeData(data) {
  const providers = data.providers || {}
  const providerNames = Object.keys(providers)
  if (providerNames.length === 0) return null

  // byEpisode: { [epNum]: { [provider]: { [dim]: { score, rationale, model } } } }
  const byEpisode = {}
  const episodeNumbers = new Set()

  for (const [pname, metrics] of Object.entries(providers)) {
    for (const m of metrics) {
      // Skip rollups and overall metrics (no episode_number)
      const details = m.calculation_details || {}
      if (!details.episode_number) continue

      const epNum = details.episode_number
      episodeNumbers.add(epNum)

      // Extract dimension key: "llm_judge_clinical_domain_coverage" -> "clinical_domain_coverage"
      const dimKey = m.metric_name.replace('llm_judge_', '')
      if (!DIMENSION_LABELS[dimKey]) continue

      if (!byEpisode[epNum]) byEpisode[epNum] = {}
      if (!byEpisode[epNum][pname]) byEpisode[epNum][pname] = {}

      byEpisode[epNum][pname][dimKey] = {
        score: m.raw_score,
        rationale: details.rationale || '',
        model: details.judge_model || 'unknown',
      }
    }
  }

  return {
    providerNames,
    episodeNumbers: [...episodeNumbers].sort((a, b) => a - b),
    byEpisode,
  }
}

export default function ReasoningTracesPanel({ journey, journeyId, refreshKey }) {
  const [activeTab, setActiveTab] = useState('generation')
  const [expandedEpisodes, setExpandedEpisodes] = useState(new Set())
  const [judgeData, setJudgeData] = useState(null)

  const episodes = journey?.episodes || []

  // Fetch judge data (re-fetch when refreshKey changes after evaluation completes)
  useEffect(() => {
    if (!journeyId) return
    evaluationAPI.getMultiJudgeComparison(journeyId)
      .then(data => {
        if (data && data.providers && Object.keys(data.providers).length > 0) {
          setJudgeData(transformJudgeData(data))
          // Auto-switch to judge tab when data arrives from a refresh
          if (refreshKey > 0) setActiveTab('judge')
        }
      })
      .catch(() => {})
  }, [journeyId, refreshKey])

  const toggleEpisode = (epNum) => {
    setExpandedEpisodes(prev => {
      const next = new Set(prev)
      if (next.has(epNum)) next.delete(epNum)
      else next.add(epNum)
      return next
    })
  }

  const expandAll = () => {
    const allNums = new Set(episodes.map(ep => ep.episode_number))
    if (judgeData) {
      judgeData.episodeNumbers.forEach(n => allNums.add(n))
    }
    setExpandedEpisodes(allNums)
  }

  const collapseAll = () => {
    setExpandedEpisodes(new Set())
  }

  // Check if any reasoning traces exist
  const hasGenerationTraces = episodes.some(ep =>
    (ep.reasoning_traces && ep.reasoning_traces.length > 0) ||
    ep.clinical_reasoning ||
    ep.nursing_reasoning
  )

  // Build episode title lookup from journey episodes
  const episodeTitles = {}
  for (const ep of episodes) {
    episodeTitles[ep.episode_number] = ep.title
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <div className="flex items-center justify-between">
          <h3 className="card-title text-lg">Reasoning Traces</h3>
          <div className="flex gap-1">
            <button className="btn btn-ghost btn-xs" onClick={expandAll}>Expand All</button>
            <button className="btn btn-ghost btn-xs" onClick={collapseAll}>Collapse All</button>
          </div>
        </div>

        <p className="text-xs text-base-content/50 mt-0.5">
          Inspect multi-model judge rationales, agent reasoning during journey generation, and clinical note writing traces.
        </p>

        {/* Tabs */}
        <div className="tabs tabs-boxed bg-base-200 mt-2">
          <button
            className={`tab tab-sm ${activeTab === 'judge' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('judge')}
          >
            Multi-Model LLM Judge
          </button>
          <button
            className={`tab tab-sm ${activeTab === 'generation' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('generation')}
          >
            Journey Generation
          </button>
          <button
            className={`tab tab-sm ${activeTab === 'clinical' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('clinical')}
          >
            Clinical Reasoning
          </button>
        </div>

        {/* Multi-Model LLM Judge Traces */}
        {activeTab === 'judge' && (
          <div className="mt-3 space-y-1.5">
            {!judgeData && (
              <div className="text-sm text-base-content/40 py-4 text-center">
                Run Multi-Model Judge evaluation to see reasoning traces.
              </div>
            )}
            {judgeData && judgeData.episodeNumbers.map(epNum => {
              const isExpanded = expandedEpisodes.has(epNum)
              const epProviders = judgeData.byEpisode[epNum] || {}
              const providerCount = Object.keys(epProviders).length

              return (
                <div key={epNum} className="rounded-lg bg-base-200 overflow-hidden">
                  {/* Clickable header */}
                  <button
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium hover:bg-base-300 transition-colors"
                    onClick={() => toggleEpisode(epNum)}
                  >
                    <span className={`text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                    <span className="badge badge-secondary badge-sm font-mono">{epNum}</span>
                    <span>{episodeTitles[epNum] || `Episode ${epNum}`}</span>
                    <span className="text-xs text-base-content/40 ml-auto">
                      {providerCount} model{providerCount !== 1 ? 's' : ''}
                    </span>
                  </button>

                  {/* Expandable content */}
                  {isExpanded && (
                    <div className="px-4 pb-3 space-y-4">
                      {judgeData.providerNames.map(pname => {
                        const providerDims = epProviders[pname]
                        if (!providerDims) return null

                        const firstDim = Object.values(providerDims)[0]
                        const modelName = firstDim?.model || 'unknown'

                        return (
                          <div
                            key={pname}
                            className={`border-l-3 ${PROVIDER_BORDER_COLORS[pname] || 'border-l-gray-400'} pl-3`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-sm font-bold ${PROVIDER_TEXT_COLORS[pname] || 'text-base-content/70'}`}>
                                {PROVIDER_LABELS[pname] || pname}
                              </span>
                              <span className="text-xs text-base-content/40">({modelName})</span>
                            </div>

                            {TIER_ORDER.map(({ tier, dims }) => {
                              const hasDims = dims.some(d => providerDims[d])
                              if (!hasDims) return null

                              return (
                                <div key={tier} className="mb-2">
                                  <div className="text-xs font-bold uppercase tracking-wider text-base-content/40 mb-1 px-0.5">
                                    {tier}
                                  </div>
                                  <div className="space-y-1.5">
                                    {dims.map(dim => {
                                      const entry = providerDims[dim]
                                      if (!entry) return null
                                      const scoreColor = entry.score >= 4 ? 'badge-success' : entry.score >= 3 ? 'badge-warning' : 'badge-error'

                                      return (
                                        <div key={dim} className="pl-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-base-content/70">
                                              {DIMENSION_LABELS[dim]}
                                            </span>
                                            <span className={`badge ${scoreColor} badge-sm font-mono`}>
                                              {entry.score?.toFixed?.(1) ?? '-'}
                                            </span>
                                          </div>
                                          {entry.rationale && (
                                            <div className="text-xs text-base-content/55 leading-relaxed mt-0.5 ml-0.5">
                                              {entry.rationale}
                                            </div>
                                          )}
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Journey Generation Traces */}
        {activeTab === 'generation' && (
          <div className="mt-3 space-y-1.5">
            {!hasGenerationTraces && (
              <div className="text-sm text-base-content/40 py-4 text-center">
                No reasoning traces available for this journey. Traces are captured when agents use &lt;thinking&gt; tags during generation.
              </div>
            )}
            {episodes.map(ep => {
              const traces = ep.reasoning_traces || []
              if (traces.length === 0) return null
              const withReasoning = traces.filter(t => t.reasoning)
              const isExpanded = expandedEpisodes.has(ep.episode_number)

              return (
                <div key={ep.episode_number} className="collapse collapse-arrow bg-base-200 rounded-lg">
                  <input
                    type="checkbox"
                    checked={isExpanded}
                    onChange={() => toggleEpisode(ep.episode_number)}
                  />
                  <div className="collapse-title text-sm font-medium py-2 min-h-0 flex items-center gap-2">
                    <span className="badge badge-primary badge-sm font-mono">{ep.episode_number}</span>
                    <span>{ep.title}</span>
                    <span className="text-xs text-base-content/40 ml-auto mr-4">
                      {traces.length} LLM calls{withReasoning.length > 0 && `, ${withReasoning.length} with reasoning`}
                    </span>
                  </div>
                  <div className="collapse-content">
                    {withReasoning.length === 0 && (
                      <div className="text-xs text-base-content/40 py-2">
                        {traces.length} LLM calls traced but no &lt;thinking&gt; tags were produced by the model. Use a larger model (e.g. gpt-4o) for detailed reasoning traces.
                      </div>
                    )}
                    <div className="space-y-2 pt-1">
                      {withReasoning.map((trace, idx) => (
                        <div
                          key={idx}
                          className={`border-l-3 ${SPEAKER_COLORS[trace.speaker] || 'border-l-gray-400'} pl-3 py-1.5`}
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-bold text-base-content/70">
                              {SPEAKER_LABELS[trace.speaker] || trace.speaker}
                            </span>
                            <span className="text-xs text-base-content/40">Turn {trace.turn_number}</span>
                          </div>
                          <div className="text-xs text-base-content/60 leading-relaxed whitespace-pre-wrap">
                            {trace.reasoning}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Clinical Reasoning Traces */}
        {activeTab === 'clinical' && (
          <div className="mt-3 space-y-1.5">
            {episodes.every(ep => !ep.clinical_reasoning && !ep.nursing_reasoning) && (
              <div className="text-sm text-base-content/40 py-4 text-center">
                No clinical reasoning traces available. These are captured from &lt;thinking&gt; tags during clinical and nursing note generation.
              </div>
            )}
            {episodes.map(ep => {
              if (!ep.clinical_reasoning && !ep.nursing_reasoning) return null
              const isExpanded = expandedEpisodes.has(ep.episode_number)

              return (
                <div key={ep.episode_number} className="collapse collapse-arrow bg-base-200 rounded-lg">
                  <input
                    type="checkbox"
                    checked={isExpanded}
                    onChange={() => toggleEpisode(ep.episode_number)}
                  />
                  <div className="collapse-title text-sm font-medium py-2 min-h-0 flex items-center gap-2">
                    <span className="badge badge-primary badge-sm font-mono">{ep.episode_number}</span>
                    <span>{ep.title}</span>
                    <div className="flex gap-1 ml-auto mr-4">
                      {ep.clinical_reasoning && <span className="badge badge-outline badge-xs">Clinical</span>}
                      {ep.nursing_reasoning && <span className="badge badge-outline badge-xs">Nursing</span>}
                    </div>
                  </div>
                  <div className="collapse-content">
                    <div className="space-y-3 pt-1">
                      {ep.clinical_reasoning && (
                        <div className="border-l-3 border-l-blue-400 pl-3 py-1">
                          <div className="text-xs font-bold text-blue-700 mb-1">Physician Clinical Reasoning</div>
                          <div className="text-xs text-base-content/60 leading-relaxed whitespace-pre-wrap">
                            {ep.clinical_reasoning}
                          </div>
                        </div>
                      )}
                      {ep.nursing_reasoning && (
                        <div className="border-l-3 border-l-teal-400 pl-3 py-1">
                          <div className="text-xs font-bold text-teal-700 mb-1">Nursing Assessment Reasoning</div>
                          <div className="text-xs text-base-content/60 leading-relaxed whitespace-pre-wrap">
                            {ep.nursing_reasoning}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
